"use client";

import React, { useEffect } from "react";
import {
  CartFragment,
  CartStatusEnum,
  CartStepEnum,
  CurrencyCode,
  ValuInstallmentPlan,
} from "@/generated/graphql";
import { reducer } from "./reducer/reducer";
import { Context } from "./context";
import { type ShippingDestination } from "@/components/UtilityComponents/ShippingDestinationSelect";
import { type PhoneInput } from "@/shared/globals/UiElements/InputPhone";
import {
  updateCartFetching as updateCartFetchingAction,
  updateCart as updateCartAction,
} from "./reducer/actionTypes";

export interface ShippingInformationProps {
  address: string;
  apartment: string;
  shippingDestination: ShippingDestination;
  postalCode: string;
  notes: string;
  secondPhone: PhoneInput;
}
export type CartContextType = CartFragment & {
  installmentPlans?: ValuInstallmentPlan[];
  isSideCartOpen?: boolean;
  isCartPaymentPopUpOpen?: boolean;
  isCartEmptyPopUpOpen?: boolean;
  isLoading?: boolean;
  isFetching?: boolean;
  recoveryCart?:
    | (CartFragment & { installmentPlans?: ValuInstallmentPlan[] })
    | null;
  shippingInformation?: ShippingInformationProps;
};

export const defaultValue: CartContextType = {
  contactInfo: {},
  cartStep: CartStepEnum.Shopping,
  lastStep: CartStepEnum.Shopping,
  id: "",
  items: [],
  createdAt: "",
  recoveryId: "",
  receipt: {
    automaticDiscount: {
      amount: 0,
      currencyCode: CurrencyCode.Egp,
    },
    subtotal: {
      amount: 0,
      currencyCode: CurrencyCode.Egp,
    },
    total: {
      amount: 0,
      currencyCode: CurrencyCode.Egp,
    },
    tax: {
      amount: 0,
      currencyCode: CurrencyCode.Egp,
    },
    discount: {
      amount: 0,
      currencyCode: CurrencyCode.Egp,
    },
    shipping: {
      amount: 0,
      currencyCode: CurrencyCode.Egp,
    },
  },
  sessionId: "",
  status: CartStatusEnum.Active,
  updatedAt: "",
  cartErrors: [],
  customer: null,
  isReserved: false,
  reservedAt: "",
  shippingDetails: null,
  isSideCartOpen: false,
  isCartPaymentPopUpOpen: false,
  isCartEmptyPopUpOpen: false,
  isLoading: false,
  isFetching: false,
  recoveryCart: null,
  shippingInformation: {
    address: "",
    apartment: "",
    notes: "",
    postalCode: "",
    secondPhone: {
      isValid: false,
      value: "",
    },
    shippingDestination: {},
  },
};

const CartContext: React.FC<{
  children: React.ReactNode;
  initialCartData: CartFragment;
  isFetching?: boolean;
}> = ({ initialCartData, isFetching, children }) => {
  const cartContextWithAdditionalData = {
    ...initialCartData,
    isSideCartOpen: false,
    isCartPaymentPopUpOpen: false,
    isCartEmptyPopUpOpen: false,
    isLoading: false,
    isFetching,
    recoveryCart: null,
    shippingInformation: {
      address: "",
      apartment: "",
      notes: "",
      postalCode: "",
      secondPhone: {
        isValid: false,
        value: "",
      },
      shippingDestination: {},
    },
  };

  const value = React.useReducer(
    reducer(defaultValue),
    cartContextWithAdditionalData
  );

  // first, we dont have cart, and then we will update the cart on client
  useEffect(() => {
    if (initialCartData && !value[0].id) {
      value[1](updateCartAction(cartContextWithAdditionalData));
    } else if (!isFetching) {
      value[1](updateCartFetchingAction(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCartData, isFetching]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export { CartContext };
